import { render } from 'preact';
import './styles/global.css';
import { App } from './app';
import { setupBackgroundSync } from './lib/backgroundSync';
import { installGlobalErrorHandlers } from './lib/errorReporting';
import { installWebVitals } from './lib/webVitals';
import { trackCaptureEvent } from './lib/captureAnalytics';

const WAITING_RETRY_INTERVAL_MS = 100;
const WAITING_RETRY_ATTEMPTS = 20;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function activateWaitingServiceWorker(
  registration: ServiceWorkerRegistration,
  options: { retryIntervalMs?: number; maxAttempts?: number } = {},
): Promise<boolean> {
  const retryIntervalMs = options.retryIntervalMs ?? WAITING_RETRY_INTERVAL_MS;
  const maxAttempts = options.maxAttempts ?? WAITING_RETRY_ATTEMPTS;

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    if (registration.waiting) {
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      return true;
    }

    await delay(retryIntervalMs);
  }

  console.warn('[sw-update] Waiting service worker not available for activation.');
  // The user asked for the update and it never arrived — they are now stuck on
  // a stale build with a button that does nothing.
  trackCaptureEvent('sw_activation_timeout', {
    surface: 'service-worker',
    attempts: maxAttempts,
    retryIntervalMs,
  });
  return false;
}

export const SW_UPDATE_AVAILABLE_EVENT = 'swUpdateAvailable';

export type ServiceWorkerActivator = () => Promise<boolean>;

export function setupServiceWorkerUpdateFlow(
  registration: ServiceWorkerRegistration,
  serviceWorker: ServiceWorkerContainer,
): void {
  let activationRequested = false;
  let updateAnnounced = false;

  const requestActivation: ServiceWorkerActivator = async () => {
    if (activationRequested || !serviceWorker.controller) {
      return false;
    }
    activationRequested = true;
    const activated = await activateWaitingServiceWorker(registration);
    if (!activated) {
      activationRequested = false;
    }
    return activated;
  };

  // The reload happens via `setupControllerChangeReload` once the user-initiated
  // activation flips the controller — we never call skipWaiting on our own,
  // because that would silently eject anyone with in-flight uploads or unsent
  // composer text on every deploy.
  const announceUpdateAvailable = () => {
    if (updateAnnounced || typeof window === 'undefined') return;
    updateAnnounced = true;
    window.dispatchEvent(
      new CustomEvent(SW_UPDATE_AVAILABLE_EVENT, {
        detail: { activate: requestActivation },
      }),
    );
  };

  // Check for updates periodically (5 min initially, catches deploys while tab is open)
  let updateInterval = 5 * 60 * 1000; // 5 minutes
  const scheduleUpdate = () => {
    setTimeout(() => {
      registration.update().catch((err) => {
        // A tab that can never see a new deploy is a silent staleness bug.
        trackCaptureEvent('sw_update_check_failed', {
          surface: 'service-worker',
          message: err instanceof Error ? err.message : String(err),
        });
      });
      updateInterval = Math.min(updateInterval * 1.5, 30 * 60 * 1000); // max 30 min
      scheduleUpdate();
    }, updateInterval);
  };
  scheduleUpdate();

  // Detect when a new SW has been installed and is waiting to activate
  registration.addEventListener('updatefound', () => {
    const newWorker = registration.installing;
    if (!newWorker) return;

    newWorker.addEventListener('statechange', () => {
      if (newWorker.state === 'installed' && serviceWorker.controller) {
        announceUpdateAvailable();
      }
    });
  });

  // Handle the case where a waiting worker already exists on page load
  if (registration.waiting && serviceWorker.controller) {
    announceUpdateAvailable();
  }
}

export function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator)) {
    return;
  }

  window.addEventListener('load', async () => {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js');
      setupServiceWorkerUpdateFlow(registration, navigator.serviceWorker);
    } catch (err) {
      // No service worker means no offline queue and no push — the two things
      // the PWA funnel depends on — and nothing reported it.
      trackCaptureEvent('sw_register_failed', {
        surface: 'service-worker',
        message: err instanceof Error ? err.message : String(err),
      });
      console.error('SW registration failed:', err);
    }
  });

  setupControllerChangeReload(navigator.serviceWorker);
}

export function setupControllerChangeReload(
  serviceWorker: ServiceWorkerContainer,
  onReload: () => void = () => window.location.reload(),
): void {
  let refreshing = false;
  serviceWorker.addEventListener('controllerchange', () => {
    if (refreshing) return;
    refreshing = true;
    // Delay slightly to allow any pending IndexedDB writes to complete
    setTimeout(onReload, 100);
  });
}

// Service workers cause reload loops in development due to repeated controllerchange events
if (!import.meta.env.DEV) {
  registerServiceWorker();
}

export function bootstrapApp(): void {
  // Install FIRST: an exception thrown during the rest of bootstrap is exactly
  // the class of failure that used to leave a blank page and no signal.
  installGlobalErrorHandlers();
  installWebVitals();

  // Set up background sync for offline contributions
  setupBackgroundSync();

  const root = document.getElementById('app');
  if (!root) {
    return;
  }

  render(<App />, root);
}

bootstrapApp();
